import { IFRAME_CONFIG } from "@src/constants";
import { fetchUserProfileApi } from "@src/api/apiService";

/**
 * Utility function to validate JWT token format
 */
function isValidJWTFormat(token: string): boolean {
    if (!token || typeof token !== "string") {
        return false;
    }

    // JWT should have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) {
        return false;
    }

    // Each part should be base64 encoded (basic check)
    try {
        for (const part of parts) {
            // JWT uses base64url encoding, but we'll do a basic validation
            if (!part || part.length === 0) {
                return false;
            }
        }
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Utility function to extract JWT payload for basic validation
 */
function getJWTPayload(token: string): any | null {
    try {
        const parts = token.split(".");
        if (parts.length !== 3) {
            return null;
        }

        // Decode the payload (middle part)
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(decoded);
    } catch (error) {
        console.warn("[IframeMessaging] Failed to decode JWT payload:", error);
        return null;
    }
}

/**
 * Check if JWT token is expired
 */
function isJWTExpired(token: string): boolean {
    const payload = getJWTPayload(token);
    if (!payload || !payload.exp) {
        return false; // If no expiry, assume it's valid
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
}

interface IframeMessage {
    type: string;
    token?: string;
    email?: string;
    data?: any;
}

interface AuthTokens {
    accessToken: string;
    refreshToken?: string;
}

class IframeMessagingService {
    private messageListeners: Map<string, (data: any) => void> = new Map();
    private eventQueue: Array<{ eventName: string; detail: any }> = [];
    private isAppReady: boolean = false;
    // Holds the last external-submit-prompt payload so ChatWidget can drain it on
    // mount, handling the race where the event fires before the component is ready.
    private bufferedSubmitPrompt: { prompt: string; type?: string } | null = null;
    // Tracks the current IDs to detect changes and drive session logic.
    private currentConsultationId: string | null = null;
    private currentPartnerProjectId: string | null = null;
    private bufferedPatientId: { partner_project_id?: string; current_consultation_id: string; latest_consultation_id?: string; total_consultation_count?: number } | null = null;
    // Tracks the last confirmed patient context so any subsequent "New Chat" action
    // (sidebar button, submitPrompt, error recovery) can attach the same context.
    // Cleared to null only when an explicit reset (empty IDs) is received from the host app.
    private activePatientContext: { partner_project_id?: string; current_consultation_id?: string; total_consultation_count?: number } | null = null;
    private isFetchingProfile: boolean = false;

    constructor() {
        this.setupMessageListener();
    }

    /**
     * Setup the main message listener for iframe communication
     */
    private setupMessageListener(): void {
        window.addEventListener("message", (event: MessageEvent) => {
            // Validate origin for security
            if (!this.isValidOrigin(event.origin)) {
                console.warn(`[IframeMessaging] Rejected message from untrusted origin: ${event.origin}`);
                return;
            }

            try {
                const message: IframeMessage = event.data;

                // Additional security validation for message contents
                if (!IFRAME_CONFIG.validateMessageSecurity(message, event.origin)) {
                    console.warn("[IframeMessaging] Message failed security validation");
                    return;
                }

                // Handle token injection from parent
                if (message.type === IFRAME_CONFIG.messageTypes.TOKEN_FROM_PARENT) {
                    this.handleTokenFromParent(message);
                }
                // Handle token refresh from parent
                if (message.type === "doctusmind-token-refresh") {
                    this.handleTokenRefresh(message);
                }
                // Handle logout from parent
                if (message.type === "doctusmind-logout") {
                    this.handleLogoutFromParent();
                }

                // Handle external prompt submitted by a partner via submitPrompt():
                // opens a new session and pastes the prompt into the input box.
                // The payload is also stored in bufferedSubmitPrompt so ChatWidget
                // can drain it on mount if the event fired before the component rendered.
                if (message.type === "external-submit-prompt") {
                    this.bufferedSubmitPrompt = message.data;
                    this.dispatchCustomEvent("iframe-submit-prompt", message.data);
                }

                // Handle partner_project_id sent by host app via setProjectId().
                if (message.type === "external-set-patient-id") {
                    this.handlesetProjectId(message);
                }

                // Call registered listeners
                const listener = this.messageListeners.get(message.type);
                if (listener) {
                    listener(message);
                }
            } catch (error) {
                console.error("[IframeMessaging] Error processing message:", error);
            }
        });
    }

    /**
     * Validate if the message origin is trusted
     */
    private isValidOrigin(origin: string): boolean {
        return IFRAME_CONFIG.isTrustedOrigin(origin);
    }

    /**
     * Get validated parent origin for secure postMessage communication
     */
    private getValidatedParentOrigin(): string | null {
        try {
            // Try to get parent origin from document.referrer
            const referrer = document.referrer;
            // TODO: for now enable for all domains, later update trusted domain
            if (referrer) {
                return new URL(referrer).origin;
            }

            // if (referrer) {
            //   const parentOrigin = new URL(referrer).origin;
            //   if (IFRAME_CONFIG.isTrustedOrigin(parentOrigin)) {
            //     return parentOrigin;
            //   }
            // }

            // // Fallback: check if we can access parent and validate its origin
            // if (window.parent && window.parent !== window) {
            //   // We can't directly access parent.location.origin due to CORS,
            //   // but we can validate against known trusted origins
            //   for (const trustedOrigin of IFRAME_CONFIG.trustedParentOrigins) {
            //     try {
            //       // This will succeed if the parent origin matches
            //       window.parent.postMessage('ping', trustedOrigin);
            //       return trustedOrigin;
            //     } catch (e) {
            //       // Continue to next trusted origin
            //     }
            //   }
            // }

            return null;
        } catch (error) {
            console.error("[IframeMessaging] Error validating parent origin:", error);
            return null;
        }
    }

    /**
     * Handle token received from parent window
     */
    private handleTokenFromParent(message: IframeMessage): void {
        if (!message.token) {
            console.warn("[IframeMessaging] No token provided in message");
            return;
        }

        // Validate JWT token format
        if (!isValidJWTFormat(message.token)) {
            console.error("[IframeMessaging] Invalid JWT token format received from partner");
            this.dispatchCustomEvent("iframe-token-error", { error: "Invalid JWT token format" });
            return;
        }

        // Check if token is expired
        if (isJWTExpired(message.token)) {
            console.error("[IframeMessaging] Expired JWT token received from partner");
            this.dispatchCustomEvent("iframe-token-error", { error: "Partner token has expired" });
            return;
        }

        // Store the validated partner token (with Safari Private mode handling)
        try {
            localStorage.setItem("accessToken", message.token);
            // console.log("[IframeMessaging] Valid partner token received and stored");
        } catch (storageError) {
            // console.warn("[IframeMessaging] localStorage blocked (Safari Private mode), token stored in memory only");
            // In Safari Private, we can't use localStorage but can still proceed with the token
        }

        // Fetch user details to get email for authentication state
        // console.log("[IframeMessaging] Starting fetchUserDetailsAndStoreEmail...");
        this.fetchUserDetailsAndStoreEmail(message.token)
            .then(() => {
                // console.log("[IframeMessaging] ✅ User details fetched successfully");
                // Check if email was stored
                // const email = localStorage.getItem("email");
                // if (email) {
                //     console.log("[IframeMessaging] ✅ User email stored:", email);
                // } else {
                //     console.log("[IframeMessaging] ⚠️ No email stored (might be guest user)");
                // }
                // CRITICAL FIX: Use dispatchCustomEvent instead of direct dispatch
                this.dispatchCustomEvent("iframe-token-received", { token: message.token });
            })
            .catch((error) => {
                console.error("[IframeMessaging] ❌ Failed to fetch user details:", error);
                // For partner tokens, authentication is required - don't proceed without valid user
                try {
                    localStorage.removeItem("accessToken");
                } catch (e) {
                    // Ignore if localStorage is blocked
                }
                this.dispatchCustomEvent("iframe-token-error", {
                    error: "Failed to validate partner token with server",
                });
            });
    }

    /**
     * Handle token refresh from parent window
     */
    private handleTokenRefresh(message: IframeMessage): void {
        if (!message.token) {
            console.warn("[IframeMessaging] No token provided in refresh message");
            return;
        }

        // Validate JWT token format
        if (!isValidJWTFormat(message.token)) {
            console.error("[IframeMessaging] Invalid JWT token format in refresh message");
            this.dispatchCustomEvent("iframe-token-error", { error: "Invalid JWT token format in refresh" });
            return;
        }

        // Check if token is expired
        if (isJWTExpired(message.token)) {
            console.error("[IframeMessaging] Expired JWT token in refresh message");
            this.dispatchCustomEvent("iframe-token-error", { error: "Refreshed token has expired" });
            return;
        }

        console.log("[IframeMessaging] Valid partner token refresh received");

        // Update token in localStorage (with Safari Private mode handling)
        try {
            localStorage.setItem("accessToken", message.token);
        } catch (storageError) {
            console.warn("[IframeMessaging] localStorage blocked, using memory-only storage");
        }

        // CRITICAL FIX: Use dispatchCustomEvent instead of direct dispatch
        this.dispatchCustomEvent("iframe-token-refreshed", { token: message.token });
    }

    /**
     * Handle logout message from parent window
     */
    private handleLogoutFromParent(): void {
        // Clear all authentication data
        localStorage.removeItem("accessToken");
        localStorage.removeItem("email");
        localStorage.removeItem("session_id");

        console.log("[IframeMessaging] Partner logout received, clearing session");
        window.dispatchEvent(new CustomEvent("iframe-logout-received"));
    }

    /**
     * Fetch user details using token and store email in localStorage
     * This maintains the existing email-based login detection logic
     */
    private async fetchUserDetailsAndStoreEmail(token: string): Promise<void> {
        if (this.isFetchingProfile) return;
        this.isFetchingProfile = true;
        try {
            // Validate token format
            if (!token || typeof token !== "string" || token.trim().length === 0) {
                console.warn("[IframeMessaging] Invalid token provided for user profile fetch");
                return;
            }

            // console.log("[IframeMessaging] Token validated, calling fetchUserProfileApi...");

            // Temporarily set token for the API call
            const originalToken = localStorage.getItem("accessToken");
            try {
                localStorage.setItem("accessToken", token);
            } catch (e) {
                console.warn("[IframeMessaging] localStorage.setItem failed (Safari Private?), continuing anyway");
            }

            // Fetch user profile with timeout
            // console.log("[IframeMessaging] Fetching user profile from API...");
            const response = await Promise.race([
                fetchUserProfileApi(),
                new Promise<never>((_, reject) => setTimeout(() => reject(new Error("API timeout")), 10000)),
            ]);

            // console.log("[IframeMessaging] API response received:", response.data);
            const userProfile = response.data;

            // Store email if user is authenticated (has email)
            if (userProfile && userProfile.email && typeof userProfile.email === "string") {
                try {
                    localStorage.setItem("email", userProfile.email);
                    // console.log("[IframeMessaging] ✅ User email stored successfully:", userProfile.email);
                } catch (e) {
                    console.warn("[IframeMessaging] Failed to store email in localStorage (Safari Private?)");
                }
            } else {
                // console.log("[IframeMessaging] ⚠️ No email found in profile - likely guest user");
                // console.log("[IframeMessaging] Profile data:", userProfile);
                // For guest users, we don't store email, maintaining guest vs logged-in distinction
            }

            // Restore original token if it existed, otherwise keep the new one
            try {
                if (originalToken) {
                    localStorage.setItem("accessToken", originalToken);
                } else {
                    localStorage.setItem("accessToken", token); // Keep the new token
                }
            } catch (e) {
                // console.warn("[IframeMessaging] localStorage operations blocked");
            }

            // console.log("[IframeMessaging] fetchUserDetailsAndStoreEmail completed successfully");
        } catch (error: any) {
            console.error("[IframeMessaging] ❌ Error fetching user profile:", error);
            console.error("[IframeMessaging] Error details:", {
                message: error?.message,
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                data: error?.response?.data,
            });

            // Handle different error scenarios gracefully
            if (error?.response?.status === 401) {
                console.warn("[IframeMessaging] Token is invalid or expired");
                // Remove invalid token
                try {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("access_token");
                } catch (e) {
                    // console.warn("[IframeMessaging] Failed to remove token from localStorage");
                }
            } else if (error?.response?.status === 403) {
                console.warn("[IframeMessaging] Access forbidden - possibly guest token");
                // Keep token but don't store email (guest user)
            } else if (error?.message === "API timeout") {
                console.warn("[IframeMessaging] API request timed out");
                // Keep token - might work later
            } else {
                console.warn("[IframeMessaging] API temporarily unavailable");
                // Keep token - network issues are usually temporary
            }

            // Re-throw to trigger the .catch() in handleTokenFromParent
            throw error;
        } finally {
            this.isFetchingProfile = false;
        }
    }

    /**
     * Send authentication token to parent window (internal use)
     * NOTE: Email is never sent to parent for privacy protection
     */
    private sendTokenToParent(tokens: AuthTokens, email?: string): void {
        try {
            const message: IframeMessage = {
                type: IFRAME_CONFIG.messageTypes.TOKEN_TO_PARENT,
                token: tokens.accessToken,
                // email is intentionally NOT included for privacy protection
                data: {
                    refreshToken: tokens.refreshToken,
                    timestamp: Date.now(),
                    origin: IFRAME_CONFIG.getCurrentOrigin(),
                },
            };

            window.parent.postMessage(message, "*");

            // Validate parent origin before sending sensitive data
            // const parentOrigin = this.getValidatedParentOrigin();
            // console.log("Parent origin",parentOrigin)
            // if (parentOrigin) {
            //   window.parent.postMessage(message, parentOrigin);
            // } else {
            //   console.warn('[IframeMessaging] Cannot send token - parent origin not trusted');
            // }
        } catch (error) {
            console.error("[IframeMessaging] Error sending token to parent:", error);
        }
    }

    /**
     * Public: Send token to parent if in iframe and not guest
     * Only call this for real user logins (not guest)
     */
    public sendTokenToParentIfNeeded(tokens: AuthTokens, email?: string): void {
        if (this.isInIframe() && email) {
            // Only send if email is present (real user)
            this.sendTokenToParent(tokens, email);
        }
    }

    /**
     * Send logout message to parent window if in iframe
     */
    public sendLogoutToParentIfNeeded(): void {
        if (this.isInIframe()) {
            try {
                window.parent.postMessage({ type: "doctusmind-logout" }, "*");
                // const parentOrigin = this.getValidatedParentOrigin();
                // if (parentOrigin) {
                //   window.parent.postMessage({ type: 'doctusmind-logout', timestamp: Date.now() }, parentOrigin);
                // } else {
                //   console.warn('[IframeMessaging] Cannot send logout - parent origin not trusted');
                // }
            } catch (error) {
                console.error("[IframeMessaging] Error sending logout to parent:", error);
            }
        }
    }

    /**
     * Enhanced logout method that clears all authentication data
     * Call this method when user logs out to ensure clean state
     */
    public performCompleteLogout(): void {
        // Clear all authentication-related localStorage items
        localStorage.removeItem("accessToken");
        localStorage.removeItem("email");
        localStorage.removeItem("session_id");

        // Notify parent window if in iframe
        this.sendLogoutToParentIfNeeded();

        // Dispatch internal logout event
        window.dispatchEvent(new CustomEvent("iframe-logout-received"));
    }

    /**
     * Request token from parent window
     */
    public requestTokenFromParent(): void {
        if (!this.isInIframe()) {
            return;
        }
        try {
            const message: IframeMessage = {
                type: IFRAME_CONFIG.messageTypes.REQUEST_TOKEN,
                data: {
                    origin: IFRAME_CONFIG.getCurrentOrigin(),
                    timestamp: Date.now(),
                },
            };
            window.parent.postMessage(message, "*");
            // const parentOrigin = this.getValidatedParentOrigin();
            // console.log("parent origin from here",parentOrigin)
            // if (parentOrigin) {
            //   window.parent.postMessage(message, parentOrigin);
            // } else {
            //   console.warn('[IframeMessaging] Cannot request token - parent origin not trusted');
            // }
        } catch (error) {
            console.error("[IframeMessaging] Error requesting token from parent:", error);
        }
    }

    /**
     * Register a custom message listener
     */
    public addMessageListener(messageType: string, callback: (data: any) => void): void {
        this.messageListeners.set(messageType, callback);
    }

    /**
     * Remove a message listener
     */
    public removeMessageListener(messageType: string): void {
        this.messageListeners.delete(messageType);
    }

    /**
     * Check if currently running in an iframe
     */
    public isInIframe(): boolean {
        return IFRAME_CONFIG.isInIframe();
    }

    /**
     * Get current iframe status and origin info
     */
    public getIframeInfo(): { isInIframe: boolean; origin: string; parentOrigin?: string } {
        const isInIframe = this.isInIframe();
        const origin = IFRAME_CONFIG.getCurrentOrigin();
        let parentOrigin: string | undefined;
        if (isInIframe) {
            try {
                parentOrigin = document.referrer ? new URL(document.referrer).origin : undefined;
            } catch (e) {
                parentOrigin = undefined;
            }
        }
        return {
            isInIframe,
            origin,
            parentOrigin,
        };
    }

    /**
     * Called by ChatWidget on mount to retrieve any external-submit-prompt payload
     * that arrived before the component's window event listener was registered.
     * Clears the buffer on read so it is only consumed once.
     */
    public consumeBufferedSubmitPrompt(): { prompt: string; type?: string } | null {
        const data = this.bufferedSubmitPrompt;
        this.bufferedSubmitPrompt = null;
        return data;
    }

    /**
     * Handle consultation data received from host app via setProjectId().
     * Fires iframe-patient-id-changed when either current_consultation_id or partner_project_id
     * is new or different, which drives session-creation logic in ChatWidget.
     * When both partner_project_id and current_consultation_id are empty the clinician has
     * left the patient screen — always dispatch so ChatWidget can start a fresh session.
     */
    private handlesetProjectId(message: IframeMessage): void {
        const incomingConsultationId: string = message.data?.current_consultation_id ?? "";
        const incomingPartnerId: string = message.data?.partner_project_id ?? "";
        const isReset = !incomingConsultationId && !incomingPartnerId;

        if (isReset) {
            // Explicit session reset — always dispatch, no de-duplication needed.
            this.currentConsultationId = "";
            this.currentPartnerProjectId = "";
            this.activePatientContext = null;
            const payload = {
                partner_project_id: "",
                current_consultation_id: "",
                latest_consultation_id: undefined as string | undefined,
                total_consultation_count: undefined as number | undefined,
            };
            this.bufferedPatientId = payload;
            console.log("[IframeMessaging] Dispatching iframe-patient-id-changed for session reset (empty IDs)");
            this.dispatchCustomEvent("iframe-patient-id-changed", payload);
            return;
        }

        const isNew =
            this.currentConsultationId !== incomingConsultationId ||
            this.currentPartnerProjectId !== incomingPartnerId;
        this.currentConsultationId = incomingConsultationId;
        this.currentPartnerProjectId = incomingPartnerId;

        if (isNew) {
            // Dispatch when either ID changes so ChatWidget creates a new session.
            const payload = {
                partner_project_id: message.data?.partner_project_id,
                current_consultation_id: incomingConsultationId,
                latest_consultation_id: message.data?.latest_consultation_id,
                total_consultation_count: message.data?.total_consultation_count,
            };
            this.activePatientContext = {
                partner_project_id: incomingPartnerId || undefined,
                current_consultation_id: incomingConsultationId,
                total_consultation_count: message.data?.total_consultation_count,
            };
            this.bufferedPatientId = payload;
            this.dispatchCustomEvent("iframe-patient-id-changed", payload);
        }
    }

    /**
     * Returns the current active patient context ({partner_project_id, current_consultation_id,
     * total_consultation_count}) that was last set by the host app via setProjectId(). Returns
     * null when no patient context is active (initial state or after an explicit reset with
     * empty IDs). Used by New Chat / session-creation paths to attach patient context to every
     * new session.
     */
    public getActivePatientContext(): { partner_project_id?: string; current_consultation_id?: string; total_consultation_count?: number } | null {
        return this.activePatientContext;
    }

    /**
     * Non-destructive check: returns true if a patient-ID payload is buffered.
     * Used by the session-creation effect to skip creating an empty session when
     * handlePatientIdChanged will create the correct patient-context session instead.
     */
    public hasBufferedPatientId(): boolean {
        return this.bufferedPatientId !== null;
    }

    /**
     * Called by ChatWidget on mount to retrieve any consultation data payload that arrived
     * before the component's event listener was registered.
     * Clears the buffer on read so it is only consumed once.
     * Returns the payload only when current_consultation_id requires a new session.
     */
    public consumeBufferedPatientId(): { partner_project_id?: string; current_consultation_id: string; latest_consultation_id?: string; total_consultation_count?: number } | null {
        const data = this.bufferedPatientId;
        this.bufferedPatientId = null;
        return data;
    }

    /**
     * CRITICAL FIX: Dispatch custom event or queue it if app not ready
     * This prevents race conditions where events fire before React listeners are registered
     */
    private dispatchCustomEvent(eventName: string, detail: any): void {
        if (this.isAppReady) {
            // App is ready, dispatch immediately
            // console.log(`[IframeMessaging] Dispatching event immediately: ${eventName}`);
            window.dispatchEvent(new CustomEvent(eventName, { detail }));
        } else {
            // App not ready, queue the event
            // console.log(`[IframeMessaging] App not ready, queuing event: ${eventName}`);
            this.eventQueue.push({ eventName, detail });
        }
    }

    /**
     * CRITICAL FIX: Call this from App.tsx when React is ready to receive events
     * This flushes any queued events that arrived before listeners were registered
     */
    public notifyAppReady(): void {
        if (this.isAppReady) {
            // console.log("[IframeMessaging] App already marked as ready, skipping");
            return;
        }

        // console.log("[IframeMessaging] 🎯 App marked as ready, flushing event queue (queue size: " + this.eventQueue.length + ")");
        this.isAppReady = true;

        // Flush all queued events
        if (this.eventQueue.length > 0) {
            // console.log(`[IframeMessaging] 📤 Flushing ${this.eventQueue.length} queued events...`);
            while (this.eventQueue.length > 0) {
                const event = this.eventQueue.shift();
                if (event) {
                    // console.log(`[IframeMessaging] 📨 Flushing queued event: ${event.eventName}`);
                    window.dispatchEvent(new CustomEvent(event.eventName, { detail: event.detail }));
                }
            }
            // console.log("[IframeMessaging] ✅ All queued events flushed");
        } else {
            // console.log("[IframeMessaging] ℹ️ No events in queue to flush");
        }

        // Send ready signal to parent with ACK capability
        try {
            // console.log("[IframeMessaging] 📡 Sending ready signal to parent widget");
            window.parent.postMessage(
                {
                    type: "doctusmind-iframe-ready",
                    timestamp: Date.now(),
                },
                "*",
            );
        } catch (error) {
            console.error("[IframeMessaging] ❌ Error sending ready signal:", error);
        }
    }
}

// Create singleton instance
export const iframeMessaging = new IframeMessagingService();

// Export types for use elsewhere
export type { IframeMessage, AuthTokens };
